import { Button } from "@/components/ui/button";
import { formatWeekdays, toggleWeekday, WEEKDAYS } from "@/pages/tina/groups/lib/weekdays";

export interface WeekdayPickerProps {
  value: number[];
  onChange: (next: number[]) => void;
}

const PRESETS: ReadonlyArray<{ label: string; days: number[] }> = [
  { label: "Weekdays", days: [1, 2, 3, 4, 5] },
  { label: "Every day", days: [1, 2, 3, 4, 5, 6, 7] },
  { label: "Weekend", days: [6, 7] },
  { label: "Clear", days: [] },
];

/**
 * Mon→Sun weekday toggle for a site group's schedule. Selected days render as a
 * solid (light) button, unselected as a filled grey button, and the presets as
 * outlined buttons — all clearly visible on the dark background — plus a
 * plain-English summary of the current selection.
 */
export function WeekdayPicker({ value, onChange }: WeekdayPickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1">
        {WEEKDAYS.map((w) => {
          const isSelected = value.includes(w.value);
          return (
            <Button
              key={w.value}
              type="button"
              variant={isSelected ? "default" : "secondary"}
              size="sm"
              className="w-12"
              aria-pressed={isSelected}
              aria-label={w.label}
              onClick={() => onChange(toggleWeekday(value, w.value))}
            >
              {w.short}
            </Button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-1">
        {PRESETS.map((preset) => (
          <Button
            key={preset.label}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(preset.days)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        {value.length > 0 ? `Runs on ${formatWeekdays(value)}` : "Pick at least one day."}
      </p>
    </div>
  );
}
